import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import sub from 'date-fns/sub';

import type {
  ApiError,
  ApiNotification,
  ApiNotificationDocumentSuccessfullyUploaded,
  ApiNotificationDocumentUploadFailedDueToVirus,
  NotificationsApi,
} from '@izimi/api';
import { ApiNotificationType } from '@izimi/api';
import { FIVE_MINUTES_MS, FIVE_SECONDS_MS } from '@izimi/utils';

import {
  useDocumentVirusScanStore,
  useInvalidateDocumentsSelectionCache,
  useInvalidateDocumentsUnSeenListCache,
  useUpdatePagedDocumentsCache,
} from '../documents';
import { useInvalidateVaultInfoCache } from '../vault';
import {
  notificationsQueryKeys,
  useGetLastNotificationsCache,
  useGetPagedNotificationsCache,
  useInvalidateLastNotificationsCache,
  useInvalidateNotificationsCache,
  useSetLastNotificationsCache,
  useSetPagedNotificationsCache,
} from './cache';
import { sortNotifications } from './helper';
import { getNotificationItems } from './notificationItems';

const createNotificationsModule = ({
  notificationsApi,
}: {
  notificationsApi: NotificationsApi;
}) => {
  const usePagedNotifications = ({ pageSize = 15 } = {}) => {
    const { data, isLoading, error, fetchNextPage, isFetchingNextPage, hasNextPage, refetch } =
      useInfiniteQuery<ApiNotification[], ApiError>({
        queryKey: notificationsQueryKeys.getPagedNotifications(),
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
          const nextNotifications = await notificationsApi.getNotifications({
            page: Number(pageParam),
            pageSize,
          });
          const sortedNotifications = sortNotifications(nextNotifications);

          return sortedNotifications;
        },
        getNextPageParam: (lastPage, allPages) =>
          lastPage.length >= pageSize ? allPages.length : undefined,
        staleTime: FIVE_MINUTES_MS,
      });

    return {
      getNotificationItems,
      notifications: data?.pages.flat(),
      error,
      isLoading,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
    };
  };

  const useMarkNotificationsAsViewed = () => {
    const queryClient = useQueryClient();

    const getPagedNotificationsCache = useGetPagedNotificationsCache();
    const setPagedNotificationsCache = useSetPagedNotificationsCache();
    const getLastNotificationsCache = useGetLastNotificationsCache();
    const setLastNotificationsCache = useSetLastNotificationsCache();

    const { mutateAsync, isPending, error } = useMutation<void, ApiError, { ids: number[] }>({
      mutationFn: ({ ids }) => notificationsApi.markNotificationsAsViewed(ids),
      onMutate: async ({ ids }) => {
        await queryClient.cancelQueries({
          queryKey: notificationsQueryKeys.getPagedNotifications(),
        });

        const pagedNotifications = getPagedNotificationsCache();
        const lastNotifications = getLastNotificationsCache();

        if (lastNotifications) {
          setLastNotificationsCache(
            lastNotifications?.map((n) => ({ ...n, viewed: ids.includes(n.id) || n.viewed })),
          );
        }

        if (pagedNotifications?.pages.length) {
          const pages = pagedNotifications.pages.reduce<ApiNotification[][]>((acc, page) => {
            acc.push(page.map((n) => ({ ...n, viewed: ids.includes(n.id) || n.viewed })));
            return acc;
          }, []);
          setPagedNotificationsCache({
            ...pagedNotifications,
            pages,
          });
        }

        return { pagedNotifications };
      },
      onError: async (_, __, context) => {
        const { pagedNotifications } = context as {
          pagedNotifications: ReturnType<typeof getPagedNotificationsCache>;
        };
        if (pagedNotifications) {
          setPagedNotificationsCache(pagedNotifications);
        }
      },
    });

    return {
      markNotificationsAsViewed: mutateAsync,
      error,
      isPending,
    };
  };

  const useDeleteNotification = () => {
    const getPagedNotificationsCache = useGetPagedNotificationsCache();
    const setPagedNotificationsCache = useSetPagedNotificationsCache();

    const { mutateAsync, isPending, error } = useMutation<void, ApiError, number>({
      mutationFn: (id) => notificationsApi.deleteNotification(id),
      onSuccess(_, id) {
        const pagedNotifications = getPagedNotificationsCache();
        if (pagedNotifications?.pages.length) {
          const pages = pagedNotifications.pages.reduce<ApiNotification[][]>((acc, page) => {
            const notification = page.find((n) => n.id === id);
            if (notification) {
              acc.push(page.filter((n) => n.id !== notification.id));
            } else {
              acc.push(page);
            }
            return acc;
          }, []);

          setPagedNotificationsCache({
            ...pagedNotifications,
            pages,
          });
        }
      },
    });

    return {
      deleteNotification: mutateAsync,
      error,
      isPending,
    };
  };

  const useLastNotifications = () => {
    const from = sub(new Date(), { weeks: 3 }).toISOString();
    const { data, isLoading, error } = useQuery({
      queryKey: notificationsQueryKeys.getLastNotifications(),
      queryFn: async () => await notificationsApi.getNotifications({ from }),
      staleTime: Infinity,
    });

    const hasUnreadNotifications = data?.some((n) => !n.viewed);

    return {
      hasUnreadNotifications,
      data,
      isLoading,
      error,
    };
  };

  const useStartListeningForDocumentVirusScanNotifications = () => {
    const { documentIds, removeDocumentIds } = useDocumentVirusScanStore();
    const updatePagedDocumentsCache = useUpdatePagedDocumentsCache();
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const invalidateNotificationsCache = useInvalidateNotificationsCache();
    const invalidateLastNotificationsCache = useInvalidateLastNotificationsCache();
    const invalidateDocumentsSelectionCache = useInvalidateDocumentsSelectionCache();
    const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();

    const from = sub(new Date(), { minutes: 5 }).toISOString();

    useQuery({
      queryKey: notificationsQueryKeys.pollVirusScanNotifications(),
      queryFn: async () => {
        const notifications = await notificationsApi.getNotifications({ from });
        const filteredNotifications = notifications.filter(
          (n) =>
            !n.viewed &&
            (n.type === ApiNotificationType.DocumentSuccessfullyUploaded ||
              n.type === ApiNotificationType.DocumentUploadFailedDueToVirus),
        ) as (
          | ApiNotificationDocumentSuccessfullyUploaded
          | ApiNotificationDocumentUploadFailedDueToVirus
        )[];

        if (filteredNotifications.some((n) => documentIds.includes(n.data.documentId))) {
          const idsToRemove = filteredNotifications.map((n) => n.data.documentId);
          removeDocumentIds(idsToRemove);

          const isDone = documentIds.every((documentId) => idsToRemove.includes(documentId));

          if (isDone) {
            invalidateDocumentsUnSeenListCache();
            invalidateDocumentsSelectionCache();
            // New document in vault, so vault size has changed.
            invalidateVaultInfoCache();
            // New notifications, so invalidate notifications cache.
            invalidateLastNotificationsCache();
            invalidateNotificationsCache();
          }

          updatePagedDocumentsCache('vaultList', (allDocuments) => {
            return allDocuments.map((document) => {
              const notification = filteredNotifications.find(
                (n) => n.data.documentId === document.id,
              );
              if (notification?.type === ApiNotificationType.DocumentSuccessfullyUploaded) {
                return {
                  ...document,
                  isBeingScanned: false,
                };
              }
              if (notification?.type === ApiNotificationType.DocumentUploadFailedDueToVirus) {
                return {
                  ...document,
                  isBeingScanned: false,
                  hasVirus: true,
                };
              }
              return document;
            });
          });
        }
        return notifications;
      },
      enabled: Boolean(documentIds.length),
      refetchInterval: FIVE_SECONDS_MS,
      staleTime: Infinity,
    });
  };

  return {
    usePagedNotifications,
    useLastNotifications,
    useMarkNotificationsAsViewed,
    useDeleteNotification,
    useStartListeningForDocumentVirusScanNotifications,
  };
};

export default createNotificationsModule;
